import { Fragment } from "react";
import type { ReactNode } from "react";
import type { Kind } from "./InlineEdit";

// Read-only formatting shared by InlineEdit and its static callsites (BasicField), so both render a
// value the same way. Its own file because a component file that also exports functions loses fast
// refresh.

// Formats a stored value for read-only display. For `type="date"` this turns
// the ISO `YYYY-MM-DD` we store into the German `DD.MM.YYYY` we show. Exported
// so static (non-edit) callsites format dates the same way the editable input
// does, otherwise a read-only date leaks as raw ISO.
export function displayValue(value: string | null | undefined, type: Kind): string | null {
  if (value == null || value === "") return null;
  if (type === "date") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  }
  return value;
}

// displayValue for rendering. A read-only value wraps like prose (the caller sets `min-w-0
// break-words`), and a token with an @ also gets break opportunities after the @ and before each
// dot, so a long address wraps as "name@" / "domain.com" instead of at whatever letter hits the edge.
// <wbr> rather than a zero-width space: it adds no character, so copying still yields the address.
export function displayText(value: string | null | undefined, type: Kind): ReactNode {
  const display = displayValue(value, type);
  if (display == null || !display.includes("@")) return display;
  return display.split(/(\s+)/).map((token, i) =>
    token.includes("@") ? (
      <Fragment key={i}>
        {token.split(/(?<=@)|(?=\.)/).map((part, j) => (
          <Fragment key={j}>
            {j > 0 ? <wbr /> : null}
            {part}
          </Fragment>
        ))}
      </Fragment>
    ) : (
      token
    ),
  );
}
